import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { OptimizePlanConfig } from '../../../../config/optimizePlanConfig';
import type { ConstraintsDataResponse } from '../queries/useConstraintsData';
import type { ObjectiveOptionType } from '../queries/useObjectiveOptions';
import type { ConstraintRowData, Level } from '../../components/OptimizePlan/types';
import { buildRowFields, computeDefaultRange } from '../../utils/optimizeConstraintHelpers';

interface UseOptimizeConstraintsParams {
    constraintsData: { data: ConstraintsDataResponse } | null;
    constraintsError: boolean;
    level: Level;
    config: OptimizePlanConfig;
    objectiveList: ObjectiveOptionType[];
    objectiveKey: string | null;
    startDate: number | null;
    open: boolean;
}

export function useOptimizeConstraints({
    constraintsData,
    constraintsError,
    level,
    config,
    objectiveList,
    objectiveKey,
    startDate,
    open,
}: UseOptimizeConstraintsParams) {
    const [tableData, setTableData] = useState<ConstraintRowData[]>([]);
    const [kpiList, setKpiList] = useState<ConstraintRowData[]>([]);
    const [constraintKpi, setConstraintKpi] = useState<ConstraintRowData | null>(null);
    const [errorExists, setErrorExists] = useState(false);

    // Config-driven: columns whose values come from the API response (excludes label and input columns).
    const dataColumns = useMemo(
        () => config.constraintTableHeaders.filter((c) => c.type !== 'label' && c.type !== 'input'),
        [config.constraintTableHeaders],
    );

    const prevConstraintsDataRef = useRef(constraintsData);
    const prevParseLevelRef = useRef<Level>(level);

    const { constraintDefaultDeltaPercent, constraintDefaultDeltaSourceKey } = config;

    const resolveRange = useCallback(
        (fields: Record<string, unknown>) =>
            computeDefaultRange(fields[constraintDefaultDeltaSourceKey], constraintDefaultDeltaPercent),
        [constraintDefaultDeltaPercent, constraintDefaultDeltaSourceKey],
    );

    // Transforms a single API constraint_detail into the flat/nested ConstraintRowData shape used by the table.
    // When isTopLevel with a single sub-item, values are inlined directly on the KPI row.
    const parseConstraintDetail = useCallback(
        (isTopLevel: boolean, dataItem: ConstraintsDataResponse['constraint_details'][number]): ConstraintRowData => {
            const levelDetails = dataItem.level_details;

            const buildSubRow = (ld: Record<string, unknown>): ConstraintRowData => {
                const fields = buildRowFields(ld, dataColumns);
                return {
                    name: (ld.attribute_name as string) ?? '',
                    label: (ld.attribute_name as string) ?? '',
                    fields,
                    ...resolveRange(fields),
                };
            };

            if (isTopLevel && levelDetails.length <= 1) {
                const first = levelDetails[0];
                const fields = first ? buildRowFields(first as unknown as Record<string, unknown>, dataColumns) : {};
                return {
                    name: dataItem.constraint_name,
                    label: dataItem.label,
                    fields,
                    ...resolveRange(fields),
                };
            }

            return {
                name: dataItem.constraint_name,
                label: dataItem.label,
                fields: {},
                min_value: null,
                max_value: null,
                levelDetails: levelDetails.map((ld) => buildSubRow(ld as unknown as Record<string, unknown>)),
            };
        },
        [dataColumns, resolveRange],
    );

    // Copies fresh fields from parsed API data onto existing tableData rows, recomputing default min/max.
    const retainKpiAndUpdateValue = useCallback(
        (parsedData: ConstraintRowData[], dataItem: ConstraintRowData): ConstraintRowData => {
            const match = parsedData.find((item) => item.name === dataItem.name);
            if (!match) return dataItem;
            return {
                ...dataItem,
                fields: match.fields,
                ...resolveRange(match.fields),
                levelDetails: match.levelDetails?.map((detail) => ({
                    ...detail,
                    ...resolveRange(detail.fields),
                })),
            };
        },
        [resolveRange],
    );

    // Re-parses constraint data whenever the API response or the level changes.
    // Separates the objective KPI from regular constraints, and merges fresh field values
    // onto any existing user-selected rows (preserving the selection but resetting editable values).
    useEffect(() => {
        if (constraintsError) {
            setErrorExists(true);
            return;
        }

        const constraintsDataChanged = constraintsData !== prevConstraintsDataRef.current;
        const levelChanged = level !== prevParseLevelRef.current;

        if (!constraintsData || (!constraintsDataChanged && !levelChanged)) return;

        prevConstraintsDataRef.current = constraintsData;
        prevParseLevelRef.current = level;

        try {
            setErrorExists(false);
            const objectiveConstraintKey = objectiveList.find((obj) => obj.value === objectiveKey)?.constraintKey;

            const isTopLevel = config.constraintLevelOptions.find((o) => o.key === level)?.depthLevel === 0;

            let newConstraintKpi: ConstraintRowData | null = null;
            const parsedData: ConstraintRowData[] = [];
            for (const item of constraintsData.data.constraint_details) {
                const parsed = parseConstraintDetail(isTopLevel, item);
                if (item.constraint_name === objectiveConstraintKey) {
                    newConstraintKpi = parsed;
                } else {
                    parsedData.push(parsed);
                }
            }
            setConstraintKpi(newConstraintKpi);

            setTableData((prevTableData) => {
                if (prevTableData.length > 0) {
                    return prevTableData.map((dataItem) => retainKpiAndUpdateValue(parsedData, dataItem));
                }
                return prevTableData;
            });

            setKpiList(parsedData);
        } catch {
            setErrorExists(true);
        }
    }, [
        constraintsData,
        constraintsError,
        level,
        config.constraintLevelOptions,
        parseConstraintDetail,
        retainKpiAndUpdateValue,
        objectiveList,
        objectiveKey,
    ]);

    useEffect(() => {
        if (objectiveKey && tableData.length > 0) {
            const objectiveConstraintKey = objectiveList.find((obj) => obj.value === objectiveKey)?.constraintKey;
            setTableData((prev) => prev.filter((item) => item.name !== objectiveConstraintKey)); // ConstraintRowData.name is set from constraint_name
        }
        // tableData.length intentionally omitted — this effect should only re-run when the
        // objective changes, not every time a row is added/removed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectiveKey, objectiveList]);

    useEffect(() => {
        if (objectiveKey && startDate && errorExists) {
            setErrorExists(false);
        }
    }, [objectiveKey, startDate, errorExists]);

    useEffect(() => {
        if (open) {
            setErrorExists(false);
        }
    }, [open]);

    const onKPISelectHandler = useCallback(
        (updatedList: string[]) => {
            const selectedSet = new Set(updatedList);
            const selectedKpiData: ConstraintRowData[] = kpiList
                .filter((kpi) => selectedSet.has(kpi.name))
                .map((kpi) => ({
                    name: kpi.name,
                    label: kpi.label,
                    fields: { ...kpi.fields },
                    ...resolveRange(kpi.fields),
                    single_value: null,
                    levelDetails: kpi.levelDetails?.map((item) => ({
                        name: item.name,
                        label: item.label,
                        fields: { ...item.fields },
                        ...resolveRange(item.fields),
                    })),
                }));
            setTableData(selectedKpiData);
        },
        [kpiList, resolveRange],
    );

    const resetConstraintTable = useCallback(() => {
        setTableData([]);
        setErrorExists(false);
        setKpiList([]);
        setConstraintKpi(null);
    }, []);

    return {
        tableData,
        setTableData,
        kpiList,
        constraintKpi,
        errorExists,
        dataColumns,
        onKPISelectHandler,
        resetConstraintTable,
    };
}
